import { useCallback } from 'react';
import { User } from '../types';
import { deleteUser as deleteUserRequest, massLock, toggleUserFlag, ToggleKind } from '../allUsersApi';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';

type FlagField = 'deactivation' | 'pos' | 'reports';

const FLAG_FIELDS: Record<ToggleKind, FlagField> = {
  activation: 'deactivation',
  pos: 'pos',
  reports: 'reports'
};

const TOGGLE_ERRORS: Record<ToggleKind, string> = {
  activation: 'Не удалось изменить статус активации',
  pos: 'Не удалось изменить статус кассы',
  reports: 'Не удалось изменить статус отчетов'
};

// Журнал действий пишет сервер (audit_log, модуль users) - здесь только запросы и состояние списка
export const useUserActions = (
  refetchUsers: () => void,
  setUsers?: React.Dispatch<React.SetStateAction<User[]>>
) => {
  const setFlag = useCallback((userId: number, field: FlagField, value: number) => {
    if (setUsers) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
    }
  }, [setUsers]);

  // Флаг меняется в таблице сразу, при отказе сервера возвращается. Связанные поля
  // (например, отчеты при блокировке кассы) приходят с повторной загрузкой списка
  const toggleFlag = useCallback(async (kind: ToggleKind, user: User) => {
    const field = FLAG_FIELDS[kind];
    const nextVal = user[field] === 0 ? 1 : 0;
    setFlag(user.id, field, nextVal);
    try {
      await toggleUserFlag(kind, user, nextVal);
      refetchUsers();
    } catch (err) {
      setFlag(user.id, field, user[field]);
      notify.error(apiErrorMessage(err, TOGGLE_ERRORS[kind]));
    }
  }, [refetchUsers, setFlag]);

  const toggleActivation = useCallback((user: User) => toggleFlag('activation', user), [toggleFlag]);
  const togglePOS = useCallback((user: User) => toggleFlag('pos', user), [toggleFlag]);
  const toggleReports = useCallback((user: User) => toggleFlag('reports', user), [toggleFlag]);

  // true - удален: карточка закрывается только после успешного ответа сервера
  const deleteUser = useCallback(async (user: User): Promise<boolean> => {
    try {
      notify.success(await deleteUserRequest(user.id));
      refetchUsers();
      return true;
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось удалить пользователя'));
      return false;
    }
  }, [refetchUsers]);

  const runMassLock = useCallback(async (kind: 'pos' | 'reports') => {
    try {
      const result = await massLock(kind);
      notify.success(result.message);
      refetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, kind === 'pos' ? 'Не удалось заблокировать кассы' : 'Не удалось заблокировать отчеты'));
    }
  }, [refetchUsers]);

  const massReportsLock = useCallback(() => runMassLock('reports'), [runMassLock]);
  const massPOSLock = useCallback(() => runMassLock('pos'), [runMassLock]);

  return {
    toggleActivation,
    togglePOS,
    toggleReports,
    deleteUser,
    massReportsLock,
    massPOSLock
  };
};
